import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import type { ContactPayload } from '@capacitor-community/contacts';
import { Contacts } from '@capacitor-community/contacts';
import { ModalService } from '@freelancer/ui';
import { ModalSize } from '@freelancer/ui/modal';
import {
  permissionReason,
  Permissions,
  PermissionStateEnum,
  PermissionType,
} from '@freelancer/ui/permissions';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy({ className: 'ContactsService' })
@Injectable({ providedIn: 'root' })
export class ContactsService {
  constructor(
    private modalService: ModalService,
    private permissions: Permissions,
  ) {}

  isSupported(): boolean {
    return Capacitor.isPluginAvailable('Contacts');
  }

  async requestPermission(skipInternalRequestModal = true): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    const isPermissionGranted = await this.permissions.requestPermissions({
      permission: PermissionType.CONTACTS,
      reason: permissionReason.CONTACTS,
      skipInternalRequestModal,
    });

    return isPermissionGranted;
  }

  async getPhoneContacts(): Promise<ContactPayload[] | undefined> {
    if (!this.isSupported()) {
      return;
    }

    const isPermissionGranted = await this.permissions.checkPermissions(
      PermissionType.CONTACTS,
    );

    if (isPermissionGranted !== PermissionStateEnum.GRANTED) {
      return;
    }

    const { contacts } = await Contacts.getContacts({
      projection: {
        name: true,
        phones: true,
      },
    });

    return contacts;
  }

  async openSyncModal(): Promise<boolean | undefined> {
    if (this.isSupported()) {
      const toSync = await this.modalService
        .open('ContactSyncModal', {
          closeable: false,
          size: ModalSize.XSMALL,
        })
        .afterClosed();

      if (toSync) {
        return this.syncContacts();
      }
    }
  }

  async syncContacts(): Promise<boolean> {
    if (!(await this.requestPermission())) {
      return false;
    }

    // TODO: T312160 - call getPhoneContacts() and implement syncing
    return true;
  }

  unsyncContacts(): boolean {
    // TODO: T312160 - implement unsyncing contacts
    return true;
  }
}
